import type { LineDefaultV0 } from '../schemas';
import type { ColorCssResolver } from '../../form/styles/themed-button.css-vars';

/**
 * CSS custom properties for the line (divider) widget — the TS twin of
 * `line.base.less` next to it, shared by the editor (websites) and the published
 * renderer (websites-builder).
 *
 * One function per variable, each taking its prop's DESKTOP leaf — the divider
 * has no per-device rendering yet. The hosts bind every variable individually
 * `[style.--x]` from its own computed (editor) or getter (published renderer).
 * Alignment is two variables rather than a modifier class because it is a margin
 * PAIR; both declarations keep an `auto` fallback, so an unset pair degrades to
 * the entity's own default (centred).
 */

type LineProps = LineDefaultV0['props'];
type Leaf<K extends keyof LineProps> = LineProps[K]['desktop'];

/** `margin-left` / `margin-right` per alignment bucket. `auto` is the flexible side. */
const ALIGN_MARGINS: Record<string, readonly [string, string]> = {
    L: ['0', 'auto'],
    M: ['auto', 'auto'],
    R: ['auto', '0'],
};

const alignMargins = (horizontalAlign: Leaf<'horizontalAlign'> | null | undefined): readonly [string, string] =>
    ALIGN_MARGINS[horizontalAlign ?? 'M'] ?? ALIGN_MARGINS['M']!;

/** Wrapper margin shorthand carried over from legacy; `null` applies none. */
export function lineWrapperMarginCss(margin: Leaf<'margin'>): string | null {
    return margin ?? null;
}

/** An empty colour removes the variable and the stylesheet keeps the entity's default colour. */
export function lineColorCss(color: Leaf<'borderTopColor'>, resolveColor: ColorCssResolver): string | null {
    return resolveColor(color);
}

export function lineStyleCss(style: Leaf<'borderTopStyle'>): string {
    return style ?? 'solid';
}

/**
 * Border thickness in px. Drives the `<hr>` border AND every metric of the
 * Safari dotted fallback (font-size, line-height, letter-spacing, height), which
 * the LESS derives with `calc()` — so the legacy JSS's four separate
 * `borderTopWidth * n` computations collapse into this one variable.
 */
export function lineThicknessCss(borderTopWidth: Leaf<'borderTopWidth'>): string {
    return `${borderTopWidth ?? 0}px`;
}

/** The divider's own width, as a percentage of the column. */
export function lineSizeCss(width: Leaf<'width'>): string {
    return `${width ?? 100}%`;
}

/** Left margin of the divider — `0` when it hugs the left edge, `auto` otherwise. */
export function lineMarginLeftCss(horizontalAlign: Leaf<'horizontalAlign'>): string {
    return alignMargins(horizontalAlign)[0];
}

/** Right margin of the divider — `0` when it hugs the right edge, `auto` otherwise. */
export function lineMarginRightCss(horizontalAlign: Leaf<'horizontalAlign'>): string {
    return alignMargins(horizontalAlign)[1];
}
